import { useEffect, useRef } from 'react';
import { BookOpen, Loader2 } from 'lucide-react';

import { useEventHandler } from '@/hooks/use-event-handler';

import { useKnowledgeBasesQuery } from '@/query/knowledge.query';

import { CreateKnowledgeBaseDialog } from './components/create-knowledge-base-dialog';
import { DocumentDetailsSheetContainer } from './components/document-details-sheet-container';
import { DocumentsList } from './components/documents-list';
import { KnowledgeBaseHeader } from './components/knowledge-base-header';
import { KnowledgeBaseSidebar } from './components/knowledge-base-sidebar';

import { useKnowledgeModalsStore } from '@/store/knowledge-modals.store';
import useModalStore from '@/store/modal.store';
import type { KnowledgeBase } from '@/types/knowledge.type';

export default function Knowledge() {
  const selectedKnowledgeBase = useKnowledgeModalsStore(state => state.selectedKnowledgeBase);
  const selectKnowledgeBase = useKnowledgeModalsStore(state => state.selectKnowledgeBase);
  const clearSelectedKnowledgeBase = useKnowledgeModalsStore(
    state => state.clearSelectedKnowledgeBase
  );
  const openModal = useModalStore(state => state.openModal);
  const previousKnowledgeBasesRef = useRef<KnowledgeBase[]>([]);

  const { data, isLoading } = useKnowledgeBasesQuery({
    page: 1,
    page_size: 100,
    query: '',
  });

  const knowledgeBases = data?.data || [];

  // Keep the current selection aligned with the list after refetches or deletions.
  useEffect(() => {
    if (!selectedKnowledgeBase) {
      if (knowledgeBases.length > 0) {
        selectKnowledgeBase(knowledgeBases[0]);
      }

      previousKnowledgeBasesRef.current = knowledgeBases;
      return;
    }

    const stillExists = knowledgeBases.some(kb => kb.kb_id === selectedKnowledgeBase.kb_id);
    const previousKnowledgeBases = previousKnowledgeBasesRef.current;
    const wasPreviouslyInList = previousKnowledgeBases.some(
      kb => kb.kb_id === selectedKnowledgeBase.kb_id
    );

    if (stillExists) {
      previousKnowledgeBasesRef.current = knowledgeBases;
      return;
    }

    if (!wasPreviouslyInList) {
      previousKnowledgeBasesRef.current = knowledgeBases;
      return;
    }

    if (knowledgeBases.length === 0) {
      clearSelectedKnowledgeBase();
      previousKnowledgeBasesRef.current = knowledgeBases;
      return;
    }

    const deletedKnowledgeBaseIndex = previousKnowledgeBases.findIndex(
      kb => kb.kb_id === selectedKnowledgeBase.kb_id
    );

    const nextKnowledgeBase =
      knowledgeBases[deletedKnowledgeBaseIndex] ||
      knowledgeBases[deletedKnowledgeBaseIndex - 1] ||
      knowledgeBases[0];

    if (nextKnowledgeBase) {
      selectKnowledgeBase(nextKnowledgeBase);
    } else {
      clearSelectedKnowledgeBase();
    }

    previousKnowledgeBasesRef.current = knowledgeBases;
  }, [clearSelectedKnowledgeBase, knowledgeBases, selectedKnowledgeBase, selectKnowledgeBase]);

  const openCreateKnowledgeBaseDialog = () => openModal(() => <CreateKnowledgeBaseDialog />);

  // Listen for create knowledge base event from page actions
  useEventHandler({
    name: 'knowledge-base:create',
    eventHandler: openCreateKnowledgeBaseDialog,
  });

  if (isLoading && !selectedKnowledgeBase) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading knowledge bases...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full gap-4">
        <KnowledgeBaseSidebar />

        <div className="flex-1 min-w-0">
          {selectedKnowledgeBase ? (
            <div className="flex flex-col h-full">
              <div className="border rounded-lg overflow-hidden">
                <KnowledgeBaseHeader />
                <DocumentsList />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-40" />
                <p className="text-sm font-medium">No Knowledge Base selected</p>
                <p className="text-xs mt-1">
                  Select a Knowledge Base from the sidebar to view its contents
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentDetailsSheetContainer />
    </>
  );
}
